/**
 * Pure diff-computation logic behind the Prompt Review Draft page
 * (PROC-75). Split out from PromptReviewDraft.tsx because that file
 * exports a component — react-refresh/only-export-components forbids
 * mixing component and non-component exports in the same file.
 */
import type { GetPromptDetailsOutput } from "@api/rest/types-and-hooks";
import type { DraftOverrides } from "@pages/Prompts/promptDraftFields";

export interface FieldChange {
  label: string;
  before: string;
  after: string;
  format?: "text" | "json";
}

/** For role/templated/technical, the draft stores a KEY but yaml source
 *  (via GetPromptDetails) stores the RESOLVED TEXT. Resolve both sides to
 *  text via the available-lookups catalog so the diff shows meaningful
 *  content. Returns the text if the key resolves; otherwise the raw
 *  value (which may itself be a literal for technical_instructions). */
export function resolveKeyOrLiteral(
  value: string | null | undefined,
  catalog: { key: string; text: string }[] | undefined,
): string {
  if (!value) return "";
  const found = catalog?.find((c) => c.key === value);
  return found ? found.text : value;
}

function inputsListAsText(inputs: { key: string }[] | undefined): string {
  if (!inputs || inputs.length === 0) return "";
  return inputs.map((i) => i.key).join("\n");
}

function yamlInputKeysAsText(input_keys: string[] | undefined): string {
  if (!input_keys || input_keys.length === 0) return "";
  // Raw yaml input_keys list — preserves nested leaves like
  // ``biographic_info.name`` / ``interviews.other`` for an accurate
  // diff against the draft's overrides.
  return input_keys.join("\n");
}

export function computeChanges(
  prompt: GetPromptDetailsOutput,
  overrides: DraftOverrides,
  rolesCatalog: { key: string; text: string }[] | undefined,
  templatedCatalog: { key: string; text: string }[] | undefined,
  technicalCatalog: { key: string; text: string }[] | undefined,
): FieldChange[] {
  const changes: FieldChange[] = [];

  const push = (label: string, before: string, after: string, format?: "text" | "json") => {
    // A trailing newline on one side isn't a change.
    if (before.trimEnd() === after.trimEnd()) return;
    changes.push({ label, before, after, format });
  };

  if ("instructions" in overrides) {
    push("Instructions", prompt.instructions ?? "", overrides.instructions ?? "");
  }
  if ("role" in overrides) {
    // Resolve BOTH sides to text so the diff means something. yaml
    // stores resolved text already; draft stores a key.
    const before = prompt.role ?? "";
    const after = resolveKeyOrLiteral(overrides.role, rolesCatalog);
    push("Role", before, after);
  }
  if ("templated_instructions" in overrides) {
    const before = prompt.templated_instructions ?? "";
    const after = resolveKeyOrLiteral(overrides.templated_instructions, templatedCatalog);
    push("Templated instructions", before, after);
  }
  if ("technical_instructions" in overrides) {
    const before = prompt.technical_instructions ?? "";
    const after = resolveKeyOrLiteral(overrides.technical_instructions, technicalCatalog);
    push("Technical instructions", before, after);
  }
  // sample_output/response_schema are DB-only and never staged here
  // going forward (PROC-190) -- they write straight to the live row
  // via the Evaluations page's Save button, with no PR step. A stray
  // key surviving from before that fix is intentionally ignored: this
  // page must not surface (or, below, re-ship) a stale draft value
  // that could clobber a newer live write.
  if ("inputs" in overrides) {
    push("Input keys", yamlInputKeysAsText(prompt.input_keys), inputsListAsText(overrides.inputs));
  }

  return changes;
}
